<script lang="ts">
  import {
    faEye,
    faEyeSlash,
    faGripVertical,
    faLock,
    faLockOpen,
    faTrash,
    faXmark,
  } from '@fortawesome/free-solid-svg-icons';
  import { ARROW_TYPES, isArrowType, isLineType, LINE_TYPES } from '$lib/core/model';
  import { BASE_LAYER } from '$lib/core/script';
  import type { EditorState } from '../editor-state.svelte';
  import { fieldKeys } from './field-keys';
  import Icon from './Icon.svelte';

  const { editor }: { editor: EditorState } = $props();

  /** Why the last rename was refused, until the next one is accepted. */
  let problem = $state<string | null>(null);

  function rename(index: number, field: HTMLInputElement) {
    problem = editor.renameLayer(index, field.value.trim());
    if (problem) field.value = editor.layers[index]?.name ?? '';
  }

  type Size = 'thick' | 'textHeight' | 'arrowSize';

  // A cleared field goes back to the default; a half-typed number changes nothing.
  function setSize(index: number, key: Size, field: HTMLInputElement) {
    if (field.value.trim() === '') editor.styleLayer(index, { [key]: undefined });
    else if (field.valueAsNumber > 0) editor.styleLayer(index, { [key]: field.valueAsNumber });
  }

  function setArrow(index: number, value: string) {
    editor.styleLayer(index, { arrow: isArrowType(value) ? value : undefined });
  }

  const titled = (word: string) => word[0]!.toUpperCase() + word.slice(1);

  function setLine(index: number, value: string) {
    editor.styleLayer(index, { line: isLineType(value) ? value : undefined });
  }

  /** The row being dragged, and where it would land: before or after the row under it. */
  let dragged = $state<number | null>(null);
  let drop = $state<{ index: number; after: boolean } | null>(null);

  // Layer 0 stays first, so nothing lands before it.
  function dragOver(event: DragEvent, index: number) {
    if (dragged === null) return;
    event.preventDefault();
    const row = event.currentTarget as HTMLElement;
    const { top, height } = row.getBoundingClientRect();
    const after = index === 0 || event.clientY > top + height / 2;
    drop = { index, after };
  }

  function dropHere() {
    if (dragged !== null && drop) {
      let to = drop.after ? drop.index + 1 : drop.index;
      if (dragged < to) to -= 1;
      editor.moveLayer(dragged, to);
    }
    endDrag();
  }

  function endDrag() {
    dragged = null;
    drop = null;
  }
</script>

{#if problem}<p class="problem" role="alert">{problem}</p>{/if}
<ul class="layers panel-list">
  {#each editor.layers as layer, index (index)}
    {@const base = layer.name === BASE_LAYER}
    <li
      class="panel-item"
      class:dragged={dragged === index}
      class:drop-before={drop?.index === index && !drop.after}
      class:drop-after={drop?.index === index && drop.after}
      ondragover={(event) => dragOver(event, index)}
      ondrop={dropHere}
    >
      <div class="line">
        {#if base}
          <span class="grip" aria-hidden="true"></span>
        {:else}
          <span
            class="grip"
            draggable="true"
            role="button"
            tabindex="-1"
            aria-label="Move {layer.name}"
            title="Drag to move layer {layer.name}; later layers paint on top"
            ondragstart={(event) => {
              dragged = index;
              event.dataTransfer?.setData('text/plain', layer.name);
            }}
            ondragend={endDrag}
          >
            <Icon icon={faGripVertical} />
          </span>
        {/if}
        <button
          type="button"
          class="icon"
          aria-label={layer.visible ? `Hide ${layer.name}` : `Show ${layer.name}`}
          title={layer.visible ? `Hide layer ${layer.name}` : `Show layer ${layer.name}`}
          aria-pressed={!layer.visible}
          onclick={() => editor.setLayerVisible(index, !layer.visible)}
        >
          <Icon icon={layer.visible ? faEye : faEyeSlash} />
        </button>
        <button
          type="button"
          class="icon"
          aria-label={layer.locked ? `Unlock ${layer.name}` : `Lock ${layer.name}`}
          title={layer.locked ? `Unlock layer ${layer.name}` : `Lock layer ${layer.name}`}
          aria-pressed={layer.locked}
          onclick={() => editor.setLayerLocked(index, !layer.locked)}
        >
          <Icon icon={layer.locked ? faLock : faLockOpen} />
        </button>
        {#if base}
          <span class="name fixed" title="Layer 0 always exists">{layer.name}</span>
        {:else}
          <input
            type="text"
            class="name"
            spellcheck="false"
            aria-label="Name of layer {index + 1}"
            value={layer.name}
            onkeydown={fieldKeys(layer.name)}
            onchange={(event) => rename(index, event.currentTarget)}
          />
          <button
            type="button"
            class="icon"
            aria-label="Delete {layer.name}"
            title="Delete layer {layer.name}"
            onclick={() => editor.removeLayer(index)}
          >
            <Icon icon={faTrash} />
          </button>
        {/if}
      </div>
      <div class="line style">
        <span class="color">
          <input
            type="color"
            class:unset={!layer.style.color}
            aria-label="Color of {layer.name}"
            title={layer.style.color ?? 'Default color'}
            value={layer.style.color ?? '#000000'}
            oninput={(event) => editor.styleLayer(index, { color: event.currentTarget.value })}
          />
          {#if layer.style.color}
            <button
              type="button"
              class="icon small"
              aria-label="Default color for {layer.name}"
              title="Use the default color"
              onclick={() => editor.styleLayer(index, { color: undefined })}
            >
              <Icon icon={faXmark} />
            </button>
          {/if}
        </span>
        <select
          aria-label="Line type of {layer.name}"
          value={layer.style.line ?? ''}
          onchange={(event) => setLine(index, event.currentTarget.value)}
        >
          <option value="">Default line</option>
          {#each Object.keys(LINE_TYPES) as line (line)}
            <option value={line}>{titled(line)}</option>
          {/each}
        </select>
        <input
          type="number"
          class="thick"
          min="0"
          step="any"
          placeholder="Thick"
          aria-label="Thickness of {layer.name}"
          title="Stroke thickness in drawing units"
          value={layer.style.thick ?? ''}
          onkeydown={fieldKeys(String(layer.style.thick ?? ''))}
          onchange={(event) => setSize(index, 'thick', event.currentTarget)}
        />
      </div>
      <div class="line style">
        <input
          type="number"
          class="size"
          min="0"
          step="any"
          placeholder="Text"
          aria-label="Dimension text height of {layer.name}"
          title="Dimension text height in drawing units"
          value={layer.style.textHeight ?? ''}
          onkeydown={fieldKeys(String(layer.style.textHeight ?? ''))}
          onchange={(event) => setSize(index, 'textHeight', event.currentTarget)}
        />
        <select
          aria-label="Dimension arrows of {layer.name}"
          title="How dimension lines end"
          value={layer.style.arrow ?? ''}
          onchange={(event) => setArrow(index, event.currentTarget.value)}
        >
          <option value="">Default arrow</option>
          {#each ARROW_TYPES as arrow (arrow)}
            <option value={arrow}>{titled(arrow)}</option>
          {/each}
        </select>
        <input
          type="number"
          class="size"
          min="0"
          step="any"
          placeholder="Arrow"
          aria-label="Dimension arrow size of {layer.name}"
          title="Dimension arrowhead length in drawing units"
          value={layer.style.arrowSize ?? ''}
          onkeydown={fieldKeys(String(layer.style.arrowSize ?? ''))}
          onchange={(event) => setSize(index, 'arrowSize', event.currentTarget)}
        />
      </div>
    </li>
  {/each}
</ul>

<style>
  .layers {
    margin: 0;
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  li {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  li.dragged {
    opacity: 0.4;
  }

  /* Shadows, not borders, so the drop marker never changes a row's height. */
  li.drop-before {
    box-shadow: inset 0 2px 0 var(--color-accent);
  }

  li.drop-after {
    box-shadow: inset 0 -2px 0 var(--color-accent);
  }

  .grip {
    display: inline-flex;
    justify-content: center;
    width: 14px;
    color: var(--color-text-muted);
    font-size: 0.75rem;
    cursor: grab;
  }

  .line {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  .style {
    padding-left: 4px;
  }

  input,
  select {
    box-sizing: border-box;
    min-width: 0;
    padding: 3px 6px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
    font-size: 0.75rem;
  }

  .name {
    flex: 1;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }

  .fixed {
    border-color: transparent;
    background: none;
    padding: 3px 6px;
    font-size: 0.75rem;
  }

  .color {
    display: flex;
    align-items: center;
  }

  input[type='color'] {
    width: 28px;
    height: 24px;
    padding: 1px;
    cursor: pointer;
  }

  input[type='color'].unset {
    opacity: 0.35;
  }

  select {
    flex: 1;
  }

  .thick {
    width: 4.5rem;
    font-variant-numeric: tabular-nums;
  }

  .size {
    width: 3.75rem;
    font-variant-numeric: tabular-nums;
  }

  button.icon {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: none;
    color: var(--color-text-muted);
    font: inherit;
    font-size: 0.75rem;
    cursor: pointer;
  }

  button.icon {
    padding: 4px 5px;
  }

  button.icon.small {
    padding: 2px 3px;
    font-size: 0.625rem;
  }

  button.icon[aria-pressed='true'] {
    color: var(--color-accent);
  }

  button.icon:hover {
    border-color: var(--color-border);
    color: var(--color-text);
  }

  .problem {
    margin: 0 0 var(--space-2);
    color: var(--color-danger);
    font-size: 0.75rem;
  }
</style>
